import '@livekit/components-styles'
import {
  RoomAudioRenderer,
  useDisconnectButton,
  useLocalParticipant,
  useRemoteParticipants,
} from '@livekit/components-react'
import { Badge } from '@workspace/ui/components/badge'
import { ConfirmButton } from '@workspace/ui/components/confirm-button'
import { SessionTimer } from '@workspace/ui/components/session-timer'
import { Loader2, LogOut } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { PdfViewer } from '@/features/preview'
import { usePdfScreenShare } from '../hooks/use-pdf-screen-share'
import { useSessionTimer } from '../hooks/use-session-timer'
import type { VideoConferenceProps } from '../types/pitch.types'
import { ParticipantTile } from './participant-tile'

function AgentConnectingCard() {
  return (
    <div className="flex h-full w-full items-center justify-center rounded-lg border-2 border-border bg-card">
      <div className="flex flex-col items-center text-center">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-[#f97316]/10">
          <Loader2 className="size-6 text-[#f97316] animate-spin" />
        </div>
        <p className="text-sm font-medium text-white">AI Investor is connecting</p>
        <p className="mt-1 text-xs text-muted-foreground">This may take a moment...</p>
      </div>
    </div>
  )
}

export function VideoConference({ pitchDeckUrl }: VideoConferenceProps) {
  const { localParticipant } = useLocalParticipant()
  const remoteParticipants = useRemoteParticipants()
  const { elapsedSeconds, resume: startTimer } = useSessionTimer(false)
  const { buttonProps } = useDisconnectButton({})

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { error } = usePdfScreenShare({ canvasRef, frameRate: 5 })

  if (error) {
    console.error('Screen share error:', error)
  }

  // Filter out agent participants - only show non-agent remote participant
  const remoteParticipant = remoteParticipants.find((p) => !p.identity.startsWith('agent-'))

  // Start timer when remote participant connects
  useEffect(() => {
    if (remoteParticipant) {
      startTimer()
    }
  }, [remoteParticipant, startTimer])

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-card/95 px-6 py-4 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <img src="/seedforge-logo.svg" alt="SeedForge" className="h-8" />
          {remoteParticipant && (
            <Badge variant="outline" size="lg" className="gap-2 py-2">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-primary" />
              </span>
              <SessionTimer seconds={elapsedSeconds} />
            </Badge>
          )}
        </div>

        {/* Leave button */}
        <ConfirmButton onConfirm={() => buttonProps.onClick?.()}>
          <LogOut className="size-4" />
          Leave
        </ConfirmButton>
      </div>

      {/* Main Content - Horizontal Split */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* PDF Viewer (Left) */}
        {pitchDeckUrl && (
          <div className="h-full min-w-0 flex-1 border-r border-border">
            <PdfViewer url={pitchDeckUrl} canvasRef={canvasRef} streamingHeight={1080} />
          </div>
        )}

        {/* Cameras (Right) - Two 4:3 cards, width = min(40%, height-derived) */}
        <div className="shrink-0 p-4 w-[min(40%,calc((100dvh-117px)*2/3+30px))]">
          <div className="flex flex-col gap-4">
            <div className="aspect-4/3 w-full">
              {localParticipant && <ParticipantTile participant={localParticipant} />}
            </div>

            <div className="aspect-4/3 w-full">
              {remoteParticipant ? (
                <ParticipantTile participant={remoteParticipant} />
              ) : (
                <AgentConnectingCard />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Audio Renderer */}
      <RoomAudioRenderer />
    </div>
  )
}
